import React, { useCallback, useEffect, useState } from 'react';
import { AuthProvider } from 'components/Auth/authContext';
import { useDispatch, useSelector } from 'react-redux';
import { loginSelector, OTPModalOpen, sessionSelector } from 'redux/modules/session/selectors';
import { AuthorizationService } from '../../services';
import { catchError, finalize } from 'rxjs/operators';
import { UsersService } from 'services/users/users';
import * as sessionActions from 'redux/modules/session/actions';
import { throwError } from 'rxjs';
import { Permission, UserProfile } from 'utils/api/models/users/userProfile';
import Cookies from 'js-cookie';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { setLoginSuccessful } from 'redux/modules/session/actions';
import { setToast } from 'redux/modules/toast/actions';
import moment from 'moment';
import { TagManager } from 'commons/gtm/tagManager';
import { unLoadQualtrics } from 'utils/qualtrics';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { enableStrongPasswordRequired, isLogin2FAEnabled, isLoginPasswordEncryptionEnabled } from 'utils/killswitch';
import { isNewDashboardEnable } from 'utils/killswitch';
import { getLoginLabels } from 'redux/modules/dynamicLabels/selectors';
import _get from 'lodash/get';
import _ from 'lodash';
import { Auth as IAuth } from 'utils/api/models/authorization/auth';
import { setTimestamp } from 'redux/modules/timestamp/actions';

export interface IUser {
	email?: string;
}

export interface IUnlockScreen {
	secret: string;
}

export interface ILogin {
	user: string;
	secret: string;
}

interface IReactNativeWebView {
	postMessage: (message: string) => void;
}

declare global {
	interface Window {
		ReactNativeWebView: IReactNativeWebView;
	}
}

window.ReactNativeWebView = window.ReactNativeWebView || {};

interface IAuthProps {
	children: React.ReactElement;
}

const Auth = ({ children }: IAuthProps) => {
	const [checkingToken, setCheckingToken] = useState(false);
	const session = useSelector(sessionSelector);
	const history = useHistory();
	const location = useLocation();
	const login = useSelector(loginSelector);
	const loginLabels = useSelector(getLoginLabels);

	const remoteConfig = useSelector(remoteConfigSelector);

	const dispatch = useDispatch();

	const domain = process.env.NODE_ENV === 'development' ? '' : '.omantel.om';
	const isStrongPasswordRequired = enableStrongPasswordRequired(remoteConfig);
	const showNewDashboard = isNewDashboardEnable(remoteConfig);
	const is2FAEnabled = isLogin2FAEnabled(remoteConfig);
	const isPasswordEncryptionEnabled = isLoginPasswordEncryptionEnabled(remoteConfig);
	const isOTPModalOpen = session.isOTPModalOpen;

	const prepareWebLocation = useCallback(() => {
		const from = (location.state as any)?.from;
		if (
			!!from &&
			from.indexOf('dashboard') !== -1 &&
			from.indexOf('login-page') !== -1 &&
			from.indexOf('lock-screen') !== -1
		) {
			history.push(from);
		} else {
			if (showNewDashboard && process.env.NODE_ENV !== 'development') {
				window.location.href = '/alpha/dashboard';
			} else {
				history.push('/dashboard');
			}
		}
	}, [history, location]);

	const checkSpecificPermission = useCallback((permissions: Permission[]) => {
		return permissions.some((per) => {
			return (
				(per.permissionExpiration && moment(per.permissionExpiration).isSameOrAfter(moment())) ||
				!per.permissionExpiration
			);
		});
	}, []);

	const checkPermissions = useCallback(
		(response: UserProfile) => {
			return (
				(response.accountPermissions?.length && checkSpecificPermission(response.accountPermissions)) ||
				(response.departmentPermissions?.length && checkSpecificPermission(response.departmentPermissions))
			);
		},
		[checkSpecificPermission]
	);

	useEffect(() => {
		// This code is used for session transfer. It's only executed when user enters in /auth/login-page
		const from = (location.state as any)?.from;

		if (
			['/auth/login-page', '/'].includes(location.pathname) &&
			!from?.indexOf('dashboard') &&
			!from?.indexOf('permission-expired')
		) {
			dispatch(showLoadingIndicator());
			setCheckingToken(true);

			const currentTimestamp = moment().format('x');
			dispatch(setTimestamp(currentTimestamp));

			UsersService.getUserProfile()
				.pipe(
					finalize(() => {
						dispatch(setToast({ active: false }));
					})
				)
				.subscribe(
					(response: UserProfile) => {
						if (response.response && checkPermissions(response)) {
							dispatch(setLoginSuccessful());
							setCheckingToken(false);
							dispatch(hideLoadingIndicator());
							if (window.ReactNativeWebView?.postMessage) {
								window.ReactNativeWebView.postMessage('loggedByToken');
								const finalLocation: string | undefined = Cookies.get('webViewFinalLocation');
								if (finalLocation) {
									history.push(finalLocation);
								} else {
									prepareWebLocation();
								}
							} else {
								prepareWebLocation();
							}
						} else {
							setCheckingToken(false);
							dispatch(hideLoadingIndicator());
							if (window.ReactNativeWebView?.postMessage) {
								window.ReactNativeWebView.postMessage('tokenExpired');
							}
							if (response.response) {
								history.push('/auth/permission-expired');
							}
						}
					},
					() => {
						if (window.ReactNativeWebView?.postMessage) {
							window.ReactNativeWebView.postMessage('emptyToken');
						}
						setCheckingToken(false);
						dispatch(hideLoadingIndicator());
					}
				);
		}
	}, [dispatch, location, history, checkPermissions, prepareWebLocation]);

	const logout = () => {
		history.push('/auth/login-page', { from: location.pathname });
		AuthorizationService.logOut().subscribe(() => {
			sessionStorage.removeItem('_cached');
			if (window.ReactNativeWebView?.postMessage) {
				window.ReactNativeWebView.postMessage('emptyToken');
			}
		});
	};

	const initiateLogin = (user: IUser): React.ReactElement => {
		const { email } = user;
		const screenLocked = Boolean(email && email.length >= 0);
		const path = screenLocked ? '/auth/lock-screen' : '/auth/login-page';
		unLoadQualtrics();
		return <Redirect to={{ pathname: path, state: { from: location.pathname } }} />;
	};

	const validateWeakPassword = (response: IAuth): boolean => {
		if (isStrongPasswordRequired && response.passwordweak) {
			dispatch(hideLoadingIndicator());
			history.push('/auth/forgot-page?forceUpdate=true', {
				from: location.pathname,
			});
			AuthorizationService.logOut(true).subscribe(
				() => {},
				() => {}
			);
			return true;
		}

		return false;
	};

	const validateAuthentication = (response: IAuth) => {
		setCheckingToken(true);
		dispatch(sessionActions.setOTPModalOpen(false));
		dispatch(sessionActions.setIsAuthenticating(true));
		let ltpaToken = response.ltpaToken;

		sessionStorage.removeItem('_cached');
		Cookies.set('correlationId', response?.correlationId, {
			secure: true,
			sameSite: 'None',
			domain,
		});

		UsersService.getUserProfile()
			.pipe(
				finalize(() => {
					setCheckingToken(false);
					dispatch(hideLoadingIndicator());
					dispatch(sessionActions.setIsAuthenticating(false));
				})
			)
			.subscribe((response: UserProfile) => {
				if (response.response && checkPermissions(response)) {
					if (window.ReactNativeWebView?.postMessage) {
						window.ReactNativeWebView.postMessage(ltpaToken);
						const finalLocation: string | undefined = Cookies.get('webViewFinalLocation');
						if (finalLocation) {
							history.push(finalLocation);
						} else {
							prepareWebLocation();
						}
					} else {
						prepareWebLocation();
					}
				} else {
					history.push('/auth/permission-expired');
				}

				if (response?.upId) {
					TagManager.dataLayer({
						dataLayer: {
							event: 'login',
							userId: response.upId,
						},
					});
				}
			});
	};

	const handleValidateLoginWithOTP = (user: string, OTP: string) => {
		dispatch(showLoadingIndicator());
		dispatch(sessionActions.setIsOTPValidation(true));
		AuthorizationService.validateLoginOTP(user, OTP)
			.pipe(
				finalize(() => {
					dispatch(sessionActions.setIsOTPValidation(false));
				}),
				catchError((error) => {
					dispatch(hideLoadingIndicator());
					return throwError(error);
				})
			)
			.subscribe(
				(response) => {
					// Check if the password is weak
					const isWeakPassword = validateWeakPassword(response);

					// Exit early if the password is weak
					if (isWeakPassword) return;

					validateAuthentication(response);
				},
				(error) => {
					const errorCode = _.get(error, 'response.errors[0].code', 'INTERNAL_ERROR');
					const errorMessageLabel =
						_get(loginLabels, `errors.${errorCode}`) ?? _get(loginLabels, 'errors.INTERNAL_SERVER_ERROR');

					const errorMessage = errorMessageLabel;

					switch (errorCode) {
						case 'INVALID_OTP':
						case 'EXPIRED_OTP':
						case 'TOKEN_MISSING':
						case 'INTERNAL_SERVER_ERROR':
							dispatch(sessionActions.setErrorMessage({ errorCode, errorMessage }));
							break;

						default:
							dispatch(sessionActions.setErrorMessage({ errorCode, errorMessage }));
							break;
					}
				}
			);
	};

	const handleAuthentication = (data: ILogin): void => {
		const { user, secret } = data;
		dispatch(showLoadingIndicator());

		AuthorizationService.signInByCredentials(user, secret, is2FAEnabled, isPasswordEncryptionEnabled).subscribe(
			(response) => {
				dispatch(sessionActions.setLoginReset());
				dispatch(sessionActions.setErrorMessage({ errorCode: '', errorMessage: '' }));

				// Check if the password is weak
				const isWeakPassword = validateWeakPassword(response);

				// Exit early if the password is weak
				if (isWeakPassword) return;

				// Check if the response contains required ltpaToken and correlationId
				const containsLoginToken = ['ltpaToken', 'correlationId'].every(
					(key) => !_.isEmpty(_.get(response, key, undefined))
				);

				// ltpaToken are present, proceed with login
				if (containsLoginToken) {
					validateAuthentication(response);
					return;
				}

				const code: string = _.get(response, 'loginCode', '');
				const errorMessageLabel =
					_get(loginLabels, `errors.${code}`) ?? _get(loginLabels, 'errors.INTERNAL_SERVER_ERROR');

				switch (code) {
					case 'OTP':
						dispatch(sessionActions.setOTPModalOpen(true));
						break;

					case 'OTP_FAIL':
						dispatch(
							sessionActions.setErrorMessage({
								errorCode: code,
								errorMessage: errorMessageLabel,
							})
						);
						dispatch(sessionActions.setOTPModalOpen(true));
						break;

					default:
						dispatch(sessionActions.setLoginUnsuccessful());
						break;
				}
			},
			(error) => {
				const errorCode = _.get(
					error,
					'response.errors[0].code',
					_.get(error, 'response.code', 'INTERNAL_ERROR')
				);
				const errorMessage =
					_get(loginLabels, `errors.${errorCode}`, undefined) ??
					_get(loginLabels, 'errors.INTERNAL_SERVER_ERROR');

				switch (errorCode) {
					case 'UNAUTHORIZED_ACCESS':
						dispatch(sessionActions.setLoginUnsuccessful());
						break;

					case 'INVALID_CREDENTIALS':
						dispatch(sessionActions.setLoginUnsuccessful());
						break;

					case 'NOT_ABLE_TO_GENERATE_CORRELATION_ID':
						dispatch(sessionActions.setLoginUnsuccessful());
						break;

					case 'INTERNAL_SERVER_ERROR':
						dispatch(sessionActions.setLoginUnsuccessful());
						break;

					default:
						dispatch(sessionActions.setLoginUnsuccessful());
						break;
				}
				dispatch(sessionActions.setErrorMessage({ errorCode, errorMessage }));
				dispatch(hideLoadingIndicator());
				return throwError(error);
			}
		);
	};

	const handleScreenUnlock = (data: IUnlockScreen): void => {
		const { secret } = data;
		handleAuthentication({ secret, user: login });
	};

	const authValue = {
		handleAuthentication,
		handleScreenUnlock,
		handleValidateLoginWithOTP,
		initiateLogin,
		logout,
		checkingToken,
		OTPModalOpen: isOTPModalOpen,
		...session,
	};

	return <AuthProvider value={authValue}>{children}</AuthProvider>;
};

export default Auth;
